// 報獎／免費遊戲字卡覆蓋層 — 以 Unity 原生 Spine 播放，對應 RewardPresentationUI 家族：
//   showFreeSpins(count)：免費遊戲進場字卡。對應 FreeSpinsUI —— 其 SkeletonData 被 LandscapeUI.prefab
//     覆寫為 ani_cutscene（小紅帽/野狼劇情動畫），播 cutscene_in_L（一次）→ cutscene_loop_L（循環）；
//     無退場動畫（_animationOut 為空，僅 0.2s 淡出）。疊加物照 FreeSpinsUI.prefab：標題（y=300）、
//     次數（TMP fontSize 200 @ 中心，本移植用 tx_win 位圖字）、FREE SPINS 字樣（y=-132）＋
//     「收集 Scatter 升級」說明字圖 tx_collectScatters（LandscapeUI 自建 CollectScattersImage，
//     (−0.6,−229.3) 984×142）；標題/字樣/說明皆優先用 Unity 原生 per-locale 字圖、未載入退回 i18n 文字。
//     （注意：ani_freespins 是盤面「+N 次數子彈」特效、非字卡，見 EffectSpines.ts。）
//   showWinCelebration(win,bet,variant,tier)：大獎慶祝 ani_win（{tier}Win{variant}_in→_loop，variant 1=主遊戲 2=FG），
//     從 Big 級起逐級跑分：**每級一律停留 in（2s）＋ loop 完整一輪（2s）＝4s**、跑分 Linear 跨滿整級，
//     點擊跳到最終值。對應 WinCelebrationUI。
//   showFreeGameSettle(win,scatter,tier)：免費遊戲整輪結算 ani_cutscene（totalWin{1-4}，依 Scatter 數選），
//     金額「直接顯示」（Unity SettleUI 無跑分）。疊加物照 SettleUI.prefab：標題字圖 (0,300) 626×90
//     依大獎與否互斥（isBigWin=tx_congratulations2／否則 tx_featureComplete，Unity SettleUI.Show 的
//     enabled 切換）、副標 tx_youHaveWon (0,152) 680×126 恆顯、「點擊任意處繼續」HintText (0,−214)。
//     對應 FreeGameSettleUI。
//   showMaxWin(win)：最高贏分 ani_maxWin（in→loop，runtime 偵測動畫名），金額直接顯示。對應 ShowMaxWin。
// 所有字卡：淡入 0.2s → 演出 → 自動關閉 5s 或點擊關閉（對應 _autoCloseTime）。
// 報獎音效（bw_scoring loop／逐級 bw_*／bw_end／maxwin 組）由 AudioManager 依 celebration* 事件播放。
// Spine 載入失敗（createXxxSpine 回 null）時整段退回自繪字卡（panel+headline+coins），行為與外觀不中斷。

import { Container, Graphics, Sprite, Text } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import type { Orientation, Resizable } from '../../../framework/render/design'
import { unityScaleLandscape, unityScalePortrait } from '../../../framework/render/design'
import { formatMoney } from '../../../framework/core/format'
import { fontFamilyForLocale, t } from '../../../framework/i18n/i18n'
import { SCATTER_SYMBOL_ID } from '../../constants'
import { getSymbolTexture } from '../../assets/SymbolTextures'
import { getUiTexture } from '../../assets/UiTextures'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { CELEBRATION_LEVEL_TIME_S, REWARD_AUTO_CLOSE_MS, type WinTier } from '../../constants'
import { BitmapNumber } from '../BitmapNumber'
import { killTweensOf, tweenTo } from '../tween'
import {
  createCutsceneSpine,
  createMaxWinSpine,
  createWinCelebrationSpine,
  loadRewardSpines,
  pickIntroLoop,
} from '../../assets/RewardSpine'
import { buildWinCelebrationPlan } from './winCelebrationPlan'

/** 字卡淡入/淡出時長（秒），對應 Unity RewardPresentationUI._fadeDuration=0.2。 */
const FADE_DURATION = 0.2

/**
 * 大獎慶祝／最高贏分字卡的金額版面，照 Unity `WinCelebrationUI.prefab` 的 `ValueText` 原值：
 *   `offsetY`＝金額中心相對**骨架原點**的下移量。ValueText 在 `Panel` 座標系為 aPos (0,−280)，
 *     而 ani_win 骨架（`Spin` 節點）aPos (0,0) 也掛在 `Panel` 中心，故兩者關係固定為 280 設計 px。
 *     （本移植把骨架放在螢幕正中，Unity 的 Panel 中心則在 canvas y=+30；因金額是相對骨架定位，
 *      美術與數字的相對關係與 Unity 一致。）
 *   `fontSize`＝TMP m_fontSizeBase 170（autoSize 18~170）、`maxWidth`＝m_SizeDelta.x 1100。
 * 直版沿用同一批節點，唯一差異來自 `CommonPanel_Portrait` 覆寫 `Panel` localScale=0.8
 * （`Spin` 另覆寫 1.25 抵銷之，故骨架實際大小與橫版相同、只有金額同乘 0.8）。
 */
const SETTLE_VALUE: Readonly<Record<Orientation, { readonly offsetY: number; readonly fontSize: number; readonly maxWidth: number }>> = {
  landscape: { offsetY: 280, fontSize: 170, maxWidth: 1100 },
  portrait: { offsetY: 224, fontSize: 136, maxWidth: 880 },
}
/** TMP fontSize → tx_win 位圖字顯示高度的換算（對照 FG 結算卡 fontSize 200 ↔ 位圖高 168）。 */
const TMP_FONT_TO_BITMAP = 0.84
/** TMP autoSize 下限（ValueText m_fontSizeMin=18）。 */
const TMP_MIN_FONT_SIZE = 18
/**
 * FG 結算卡（ani_cutscene totalWin{1-4}）金額垂直微調（設計 px，正值＝往下；P4 使用者需求＝
 * 贏分數字往下移一點、不要遮住狼）。ani_cutscene 的角色（離線量測 totalWin1_loop 姿勢：
 * wolf1 y≈[−283,+316]、redRidingHood [−143,+222]；y 向下＝負為上）臉部落在畫面中央偏上，
 * 而金額原本置於骨架原點（螢幕中央 y=0），墨跡框約 [−84,+84]·s 正好蓋住狼的口鼻/胸口。
 * 下移 90 設計 px → 墨跡框約 [+6,+164]·s，落在角色下半／地面帶、露出狼臉，且仍在
 * 「點擊任意處」提示（HintText +214·s、字高半 ~19）之上（+164 < +195，留約 21 設計 px 間隙）。
 * ⚠️ Unity 的 totalWin_txt 錨點骨在 y=−50（螢幕中央「上方」，離線 scripts/measure-multiplier-txt.mjs
 *    同法解得）恰落在狼臉上，與本移植的美術構圖及使用者「往下」需求相反，故不採該骨、改用此下移量。
 *    此值為 code-only 估計（依角色/提示垂直帶量測界定上限，未經瀏覽器實機微調），已具名以便日後微調。
 */
const FG_SETTLE_NUMBER_OFFSET_Y = 90
/** 大獎分級標題（i18n key `tier.{tier}`，none 為一般結束；max 為最高贏分）。fallback 自繪用。 */
function tierHeadline(tier: WinTier): string {
  return t(`tier.${tier}`)
}

/** 大獎分級標題色（fallback 自繪用；呼應暖金→火紅漸強，max 為醒目洋紅）。 */
const TIER_COLOR: Record<WinTier, number> = {
  none: 0xffe45c,
  big: 0xffd23c,
  mega: 0xffb03c,
  super: 0xff8a3c,
  epic: 0xff5a3c,
  max: 0xff3c8a,
}

export class RewardOverlayView extends Container implements Resizable {
  private readonly dim = new Graphics()
  /** Spine 演出層（四支報獎骨架，各自 visible 控制）。 */
  private readonly spineLayer = new Container()
  // ⚠️ 這三支骨架（約 10MB）不在首屏載入，於首次報獎前由 ensureSpines() 建立，故不可為 readonly。
  private winSpine: Spine | null = null
  private cutsceneSpine: Spine | null = null
  private maxWinSpine: Spine | null = null
  /** 骨架是否已建立（ensureSpines 的冪等旗標）。 */
  private spinesBuilt = false
  /** fallback 自繪元件（Spine 不可用時才顯示）。 */
  private readonly panel = new Graphics()
  private readonly coinLayer = new Container()
  private readonly headline = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 48, fill: 0xffe45c, fontWeight: 'bold', stroke: { color: 0x4a1206, width: 6 }, align: 'center' },
  })
  private readonly captionTop = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 24, fill: 0xffffff, fontWeight: 'bold', stroke: { color: 0x2b0f38, width: 3 } },
  })
  /** 大獎金額／免費次數數字，用 Unity tx_win 位圖字型（BitmapNumber）呈現，疊在 Spine 前。 */
  private readonly bigNumber = new BitmapNumber()
  private readonly captionBottom = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 28, fill: 0xffffff, fontWeight: 'bold', stroke: { color: 0x2b0f38, width: 4 } },
  })
  // ── 免費遊戲進場字卡專用元素（對照 Unity FreeSpinsUI 版面，其餘模式隱藏） ──
  /** 「你贏得了」（金字，位於恭喜與次數之間）。 */
  private readonly youWon = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 48, fill: 0xffe45c, fontWeight: 'bold', stroke: { color: 0x4a1206, width: 5 }, align: 'center' },
  })
  /** 「收集」白字（收集說明行左半，其後緊接 Scatter 圖示）。 */
  private readonly collectPre = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 26, fill: 0xffffff, fontWeight: 'bold', stroke: { color: 0x2b0f38, width: 3 } },
  })
  /** 收集說明行的 Scatter 圖示（inline，介於「收集」與「以進行升級…」之間）。 */
  private readonly scatterIcon = new Sprite()
  /** 「以進行升級 - 最高可達 3 級」白字（收集說明行右半）。 */
  private readonly collectPost = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 26, fill: 0xffffff, fontWeight: 'bold', stroke: { color: 0x2b0f38, width: 3 } },
  })
  /** 「贏取更高的乘數與免費旋轉獎勵！」白字（收集說明第二行）。 */
  private readonly collectReward = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 26, fill: 0xffffff, fontWeight: 'bold', stroke: { color: 0x2b0f38, width: 3 }, align: 'center' },
  })
  // ── 字卡的 Unity 原生本地化字圖（S026_AssetTable per-locale；載入成功時取代手寫文字）──
  /** 進場卡「恭喜」（Unity FreeSpinsUI Congratulations 節點，(0,300) 924×246）。 */
  private readonly introTitleSprite = new Sprite()
  /** 「你贏得了」（YouHaveWonImage，(0,152) 680×126——進場卡與 FG 結算卡同座標同尺寸，共用）。 */
  private readonly youHaveWonSprite = new Sprite()
  /** 進場卡「免費旋轉」（Unity FreeSpinsImage，(0,−132) 415×96）。 */
  private readonly introFreeSpinsSprite = new Sprite()
  /** FG 結算卡標題（isBigWin→tx_congratulations2「恭喜」／否則 tx_featureComplete「特色完成」；
   *  Unity SettleUI 兩節點同座標 (0,300) 626×90 互斥顯示，本移植以單一 Sprite 換貼圖）。 */
  private readonly settleTitleSprite = new Sprite()
  /** 進場卡「收集 Scatter 升級」兩行說明字圖（tx_collectScatters；LandscapeUI 自建 CollectScattersImage
   *  橫版 (−0.6,−229.3) 984×142、直版 PoriraitUI (0.8,−182.8) ×0.8）。未載入時退回 collect* 文字行。 */
  private readonly collectBanner = new Sprite()

  private screenW = 0
  private screenH = 0
  /** 目前螢幕方向（直版動畫用 _P 變體、慶祝一律變體 2，照 PoriraitUI 覆寫）。 */
  private orientation: Orientation = 'landscape'
  /** 目前字卡類型，供 layout 決定文字位置（settle=大獎慶祝/最高贏分、fgSettle=FG 整輪結算卡）。 */
  private mode: 'intro' | 'settle' | 'fgSettle' = 'intro'
  /** 目前是否為 Spine 演出（true 時隱藏自繪 chrome、數字置於 Spine 前）。 */
  private spineActive = false
  /** 目前顯示中的 Spine（供 layout 定位）。 */
  private activeSpine: Spine | null = null
  /** 數字滾動 tween 對象。 */
  private readonly numberProxy = { value: 0 }
  /** 跑分中點擊「跳到最終值」的旗標（waitForDismiss 前的點擊）。 */
  private skipRequested = false
  /**
   * 報獎點擊階段：false＝演出中（首次點擊＝快轉到最終階段）、true＝已達最終（點擊＝關閉）。
   * 對應需求「報獎時點擊快速略過到最終階段、再次點擊關閉」。逐段跑分的大獎慶祝首擊只略過不關閉。
   */
  private phaseSettled = true
  /** waitForDismiss 的 resolver（達最終階段後點擊或逾時觸發關閉）。 */
  private dismissResolver: (() => void) | null = null
  /**
   * 在 waitForDismiss 掛上 resolver **之前**（0.2s 淡入期間）就按下的關閉點擊，先記著、等 waitForDismiss
   * 一開始就消化掉。否則字卡剛出現時的點擊會被靜默吞掉，玩家會以為「點了沒反應」。
   */
  private pendingDismiss = false

  constructor(private readonly bus?: EventBus<GameEventMap>) {
    super()
    this.visible = false

    this.headline.anchor.set(0.5)
    this.captionTop.anchor.set(0.5)
    this.captionBottom.anchor.set(0.5)
    this.youWon.anchor.set(0.5)
    this.collectReward.anchor.set(0.5)
    // 收集說明行 inline 三件（左半文字／圖示／右半文字）：左錨定、垂直置中，於 layout 依寬度水平排列置中。
    this.collectPre.anchor.set(0, 0.5)
    this.scatterIcon.anchor.set(0, 0.5)
    this.collectPost.anchor.set(0, 0.5)

    for (const sprite of [this.introTitleSprite, this.youHaveWonSprite, this.introFreeSpinsSprite, this.settleTitleSprite, this.collectBanner]) {
      sprite.anchor.set(0.5)
      sprite.visible = false
    }

    this.dim.eventMode = 'static'
    // ⚠️ 點擊監聽必須掛在**本容器**上，不可只掛在 dim 上：PixiJS v8 的 EventBoundary 由上而下（topmost-first）
    //    逐一遞迴子節點，而 **passive 節點只要幾何上蓋住該點就會回傳空陣列 `[]`（truthy）並中止搜尋**，
    //    使 hit target 變成本容器、永遠輪不到底層的 dim。本字卡的 spineLayer／數字／字圖全都疊在 dim 之上且
    //    覆蓋畫面中央，因此掛在 dim 的監聽實測完全不會觸發（進場字卡與報獎字卡都點不掉）。
    //    掛在容器上則兩種情形都成立：dim 被命中時事件會冒泡上來，被 passive 子節點截斷時本容器就是 target。
    this.eventMode = 'static'
    this.on('pointertap', () => this.handleTap())
    this.addChild(
      this.dim,
      this.spineLayer,
      this.panel,
      this.coinLayer,
      this.headline,
      this.captionTop,
      this.bigNumber,
      this.captionBottom,
      this.youWon,
      this.collectPre,
      this.scatterIcon,
      this.collectPost,
      this.collectReward,
      this.introTitleSprite,
      this.youHaveWonSprite,
      this.introFreeSpinsSprite,
      this.settleTitleSprite,
      this.collectBanner,
    )
  }

  /**
   * 套用進場字卡的 Unity 原生字圖（依當前語系載入的 tx_congratulations/tx_youHaveWon/tx_winFreeSpins）。
   * 成功時隱藏對應手寫文字；貼圖未載入時回 false（呼叫端維持文字版）。
   */
  private applyIntroSprites(): boolean {
    try {
      this.introTitleSprite.texture = getUiTexture('txCongrats')
      this.youHaveWonSprite.texture = getUiTexture('txYouHaveWon')
      this.introFreeSpinsSprite.texture = getUiTexture('txWinFreeSpins')
      this.introTitleSprite.visible = true
      this.youHaveWonSprite.visible = true
      this.introFreeSpinsSprite.visible = true
      return true
    } catch {
      return false
    }
  }

  /**
   * 套用 FG 結算卡的 Unity 原生字圖（SettleUI.prefab）：標題依 isBigWin 互斥選
   * tx_congratulations2／tx_featureComplete（對應 SettleUI.Show 的 enabled 切換）、
   * 副標 tx_youHaveWon 恆顯。貼圖未載入時回 false（維持既有純數字版面）。
   */
  private applySettleSprites(isBigWin: boolean): boolean {
    try {
      this.settleTitleSprite.texture = getUiTexture(isBigWin ? 'txCongrats2' : 'txFeatureComplete')
      this.youHaveWonSprite.texture = getUiTexture('txYouHaveWon')
      this.settleTitleSprite.visible = true
      this.youHaveWonSprite.visible = true
      return true
    } catch {
      return false
    }
  }

  /** 套用進場卡「收集 Scatter 升級」說明字圖（tx_collectScatters）；未載入回 false（呼叫端維持文字行）。 */
  private applyCollectBanner(): boolean {
    try {
      this.collectBanner.texture = getUiTexture('txCollectScatters')
      this.collectBanner.visible = true
      return true
    } catch {
      return false
    }
  }

  /**
   * 覆蓋層點擊分派（對應需求：報獎點擊快速略過到最終階段、再次點擊關閉）：
   *   演出中（phaseSettled=false）首次點擊 → 設 skipRequested 快轉到最終階段、進入「已達最終」；
   *   已達最終（phaseSettled=true）→ 觸發關閉。
   */
  private handleTap(): void {
    if (!this.phaseSettled) {
      this.skipRequested = true
      this.phaseSettled = true
      return
    }
    if (this.dismissResolver === null) {
      // 尚在淡入、waitForDismiss 還沒掛上 resolver：記下這次點擊，等 waitForDismiss 一開始就關閉。
      this.pendingDismiss = true
      return
    }
    this.dismissResolver()
  }

  /**
   * 進場字卡：免費遊戲次數。對應 Unity FreeSpinsUI.Show(count) —— Spine 為 ani_cutscene
   * （cutscene_in_L 一次 → cutscene_loop_L 循環，LandscapeUI.prefab 覆寫值），上面疊
   * 標題／次數／FREE SPINS／點擊提示（FreeSpinsUI.prefab 的 Congratulations/ValueText/
   * FreeSpinsImage/HintText 版面）。5s 自動關閉或點擊任意處關閉；退場僅 0.2s 淡出。
   */
  /**
   * 確保三支報獎骨架已就緒。這批資產（約 10MB）不在首屏載入（見 main.ts 的背景載入），
   * 因此每個 show* 進來時先等背景任務、再補建骨架與版面。可重複呼叫；資產不可用時
   * 三個欄位維持 null，各 show* 自動走既有的自繪 fallback。
   */
  private async ensureSpines(): Promise<void> {
    if (this.spinesBuilt) return
    await loadRewardSpines()
    if (this.spinesBuilt) return
    this.spinesBuilt = true
    this.winSpine = createWinCelebrationSpine()
    this.cutsceneSpine = createCutsceneSpine()
    this.maxWinSpine = createMaxWinSpine()
    for (const s of [this.winSpine, this.cutsceneSpine, this.maxWinSpine]) {
      if (s !== null) {
        s.visible = false
        this.spineLayer.addChild(s)
      }
    }
    // 骨架是在 resize() 之後才生出來的，補一次版面（beginSpine 也會排版，但 legacy fallback 路徑不會）。
    this.layout()
  }

  async showFreeSpins(count: number): Promise<void> {
    await this.ensureSpines()
    if (this.cutsceneSpine !== null) {
      this.beginSpine(this.cutsceneSpine, 'intro')
      // 直版動畫變體照 PoriraitUI 覆寫（cutscene_in_P/cutscene_loop_P）。
      const sfx = this.orientation === 'portrait' ? 'P' : 'L'
      this.playSpineAnim(this.cutsceneSpine, `cutscene_in_${sfx}`, `cutscene_loop_${sfx}`)
      // FreeSpinsUI 疊加（對照 Unity 版面：恭喜／你贏得了／次數／免費旋轉／收集 Scatter 升級說明兩行／
      // 自動開始提示）。標題三件優先用 Unity 原生 per-locale 字圖（tx_congratulations/tx_youHaveWon/
      // tx_winFreeSpins，S026_AssetTable 的 runtime 換綁），未載入時退回 i18n 文字。
      const useSprites = this.applyIntroSprites()
      this.headline.text = t('reward.congrats')
      this.setHeadlineColor(0xffe45c)
      this.headline.visible = !useSprites
      this.youWon.text = t('reward.youHaveWon')
      this.youWon.visible = !useSprites
      this.captionBottom.text = t('common.Buy_FreeSpins')
      this.captionBottom.visible = !useSprites
      // 收集說明第一行（「收集」＋Scatter 圖示＋「以進行升級 - 最高可達 3 級」）＋第二行。
      // 收集說明照 Unity 為單張兩行字圖 tx_collectScatters（CollectScattersImage 節點）；
      // 未載入時退回「收集＋Scatter 圖示＋以進行升級…／贏取更高的乘數…」文字行版面。
      if (!this.applyCollectBanner()) {
        this.collectPre.text = t('reward.collectScatterPre')
        this.collectPre.visible = true
        this.applyScatterIcon()
        this.collectPost.text = t('reward.collectScatterPost')
        this.collectPost.visible = true
        this.collectReward.text = t('reward.collectScatterReward')
        this.collectReward.visible = true
      }
      // 底部提示改用 Unity 權威字串「遊戲將在 5 秒後自動開始」（對應 mediator _autoCloseTime=5）。
      this.captionTop.text = t('common.Continue_Hint')
      this.captionTop.visible = true
      this.bigNumber.setText(`${count}`)
      this.layout()
      await this.fadeIn()
      this.popBigNumber()
      await this.waitForDismiss(REWARD_AUTO_CLOSE_MS)
      await this.fadeOut()
      return
    }
    // fallback 自繪
    this.beginFallback('intro')
    this.headline.text = t('reward.congrats')
    this.setHeadlineColor(0xffe45c)
    this.captionTop.text = ''
    this.captionTop.visible = false
    this.bigNumber.setText(`${count}`)
    this.captionBottom.text = t('reward.freeSpins')
    this.captionBottom.visible = true
    this.clearCoins()

    this.layout()
    await this.fadeIn()
    this.popBigNumber()
    await this.waitForDismiss(REWARD_AUTO_CLOSE_MS)
    await this.fadeOut()
  }

  /**
   * 大獎慶祝（ani_win）：從 Big 級起逐級跑分到目標級，每級播 {tier}Win{variant}_in→_loop。
   * variant 1=主遊戲、2=免費遊戲（對應 Unity CustomWinCelebrationUI 依 IsFreeGame 選動畫組）。
   * 自動播放節奏：每級一律停留 `_in`（2s）＋ `_loop` 完整一輪（2s）＝4s，跑分 Linear 跨滿整級
   * （不做 Unity 末級的比例縮短，見 stageHoldSeconds）；點擊畫面＝跳到最終值並切目標級動畫。
   * 未達 big（<10×bet）不應呼叫（呼叫端已守衛）。
   */
  async showWinCelebration(totalWin: number, totalBet: number, variant: 1 | 2, tier: WinTier): Promise<void> {
    await this.ensureSpines()
    // 直版一律用變體 2（PoriraitUI/CommonPanel_Portrait 覆寫 _winAnimationDatas 為 bigWin2/megaWin2/…）。
    const effectiveVariant: 1 | 2 = this.orientation === 'portrait' ? 2 : variant
    const plan = buildWinCelebrationPlan(totalWin, totalBet, effectiveVariant, tier)
    if (this.winSpine === null || plan.length === 0) {
      this.bus?.emit('celebrationStarted', { tier })
      await this.legacySettle(totalWin, tier)
      this.bus?.emit('celebrationEnded', { tier })
      return
    }
    this.beginSpine(this.winSpine, 'settle')
    this.skipRequested = false
    // 逐段跑分階段：首次點擊只略過到最終、不關閉（phaseSettled=false）。
    this.phaseSettled = false
    this.bus?.emit('celebrationStarted', { tier })
    // ⚠️ 先掛第一段的 in→loop 再淡入：beginSpine 只切 visible、**不重置骨骼動畫軌**，而 winSpine 是
    //    複用同一支骨架（上一張字卡結束時停在 `{lastTier}Win{v}_loop` 且 autoUpdate 續播）。若等到迴圈內
    //    （fadeIn 之後）才 setAnimation，0.2s 淡入期間就會露出上一張殘留的 loop ——即使用者回報「報獎一開
    //    就誤播 big win loop 的觀感」。改在淡入前掛好第一段的 _in，淡入時看到的就是正確的進場動畫。
    const first = plan[0]!
    this.playSpineAnim(this.winSpine, `${first.animation}_in`, `${first.animation}_loop`)
    await this.fadeIn()

    for (let i = 0; i < plan.length; i++) {
      const seg = plan[i]!
      if (this.skipRequested) {
        // 跳到最終值＋目標級動畫，結束跑分。
        const last = plan[plan.length - 1]!
        this.bus?.emit('celebrationSegment', { tier: last.tier })
        this.playSpineAnim(this.winSpine, `${last.animation}_in`, `${last.animation}_loop`)
        this.setNumber(totalWin)
        break
      }
      this.bus?.emit('celebrationSegment', { tier: seg.tier })
      // 第一段已於淡入前掛好，這裡不重掛（重掛會把 _in 從頭再播一次而頓一下）；其餘段照序切換。
      if (i > 0) this.playSpineAnim(this.winSpine, `${seg.animation}_in`, `${seg.animation}_loop`)
      // 每級停留＝_in 播完＋_loop 完整一輪（各 2s）；跑分同步跨滿整級，不縮短、不瞬跳。
      await this.countUpBetween(seg.from, seg.to, this.stageHoldSeconds(seg.animation))
    }
    this.setNumber(totalWin)
    // 跑分結束、金額定案後重排一次，對最終值套用 ValueText 1100 設計寬上限（TMP autoSize 等價）。
    this.layout()
    // 已達最終：接下來點擊即關閉。
    this.phaseSettled = true
    this.bus?.emit('celebrationEnded', { tier })
    await this.waitForDismiss(REWARD_AUTO_CLOSE_MS)
    await this.fadeOut()
  }

  /**
   * 一級報獎的停留時長（秒）＝ `_in` 播完 ＋ `_loop` 完整跑一輪，取骨架動畫的實際長度
   * （ani_win 全 8 組 in/loop 皆為 2.000s，故每級固定 4s）；查不到動畫時退回 CELEBRATION_LEVEL_TIME_S。
   *
   * ⚠️ 與 Unity WinCelebrationUI 的差異：Unity 末級按 `(剩餘/該級區間)*LevelPlayTime` 比例縮短
   * （實測 300/bet2 的 epic 級只剩 2s，區間內金額少時甚至 ≤1s 直接瞬跳），末級的 in/loop 因此播不完整。
   * 依使用者要求改為每級一律播滿，自動播放時各級節奏一致。
   */
  private stageHoldSeconds(animation: string): number {
    const data = this.winSpine?.skeleton.data
    const intro = data?.findAnimation(`${animation}_in`)?.duration ?? 0
    const loop = data?.findAnimation(`${animation}_loop`)?.duration ?? 0
    const total = intro + loop
    return total > 0 ? total : CELEBRATION_LEVEL_TIME_S
  }

  /** 最高贏分（ani_maxWin，in→loop）。金額直接顯示（對應 Unity RewardPresentationUI.Show 直設 TMP）。 */
  async showMaxWin(totalWin: number): Promise<void> {
    await this.ensureSpines()
    this.bus?.emit('celebrationStarted', { tier: 'max' })
    const anims = this.maxWinSpine !== null ? pickIntroLoop(this.maxWinSpine) : null
    if (this.maxWinSpine === null || anims === null) {
      await this.legacySettle(totalWin, 'max')
      this.bus?.emit('celebrationEnded', { tier: 'max' })
      return
    }
    this.beginSpine(this.maxWinSpine, 'settle')
    this.playSpineAnim(this.maxWinSpine, anims.intro, anims.loop)
    this.setNumber(totalWin)
    // setNumber 改變數字寬度後重排一次，套用 ValueText 1100 設計寬上限（TMP autoSize 等價）。
    // 最高贏分金額不跑分（直接顯示），沒有這次重排會沿用 beginSpine 時的舊寬度而不縮字。
    this.layout()
    await this.fadeIn()
    this.popBigNumber()
    await this.waitForDismiss(REWARD_AUTO_CLOSE_MS)
    this.bus?.emit('celebrationEnded', { tier: 'max' })
    await this.fadeOut()
  }

  /**
   * 免費遊戲整輪結算（ani_cutscene）：依整輪累積 Scatter 數選 totalWin{1-4}（index=clamp(⌊scatter/2⌋,0,3)）。
   * 金額直接顯示、無跑分（對應 Unity SettleUI 走 RewardPresentationUI.Show 直設值）。對應 FreeGameSettleUI.Show。
   * 疊加物照 SettleUI.prefab：標題字圖依大獎與否互斥（isBigWin＝totalWin/bet≥10 ⇔ tier!=='none'，
   * max 走 showMaxWin 不進此處）、副標「你贏得了」恆顯、「點擊任意處繼續」提示。
   */
  async showFreeGameSettle(totalWin: number, scatterCount: number, tier: WinTier): Promise<void> {
    await this.ensureSpines()
    if (this.cutsceneSpine === null) {
      await this.legacySettle(totalWin, tier)
      return
    }
    const index = Math.max(0, Math.min(3, Math.floor(scatterCount / 2)))
    const base = `totalWin${index + 1}`
    this.beginSpine(this.cutsceneSpine, 'fgSettle')
    this.applySettleSprites(tier !== 'none')
    this.captionTop.text = t('reward.pressAnywhere')
    this.captionTop.visible = true
    this.playSpineAnim(this.cutsceneSpine, `${base}_in`, `${base}_loop`)
    this.setNumber(totalWin)
    // setNumber 改變數字寬度後重排一次，套用 ValueText 1000 設計寬上限（TMP autoSize 等價）。
    this.layout()
    await this.fadeIn()
    this.popBigNumber()
    await this.waitForDismiss(REWARD_AUTO_CLOSE_MS)
    await this.fadeOut()
  }

  // ── Spine 演出共用 ──

  /** 隱藏各字卡專用元素（進場卡文字/字圖、FG 結算卡標題字圖）；每次開卡（beginSpine/beginFallback）先全部重置。 */
  private hideCardExtras(): void {
    this.introTitleSprite.visible = false
    this.youHaveWonSprite.visible = false
    this.introFreeSpinsSprite.visible = false
    this.settleTitleSprite.visible = false
    this.collectBanner.visible = false
    this.youWon.visible = false
    this.collectPre.visible = false
    this.scatterIcon.visible = false
    this.collectPost.visible = false
    this.collectReward.visible = false
  }

  /** 設定收集說明行的 Scatter 圖示（用一般 SS 符號貼圖）；符號貼圖尚未載入時隱藏圖示（僅顯示文字）。 */
  private applyScatterIcon(): void {
    try {
      this.scatterIcon.texture = getSymbolTexture(SCATTER_SYMBOL_ID, 1)
      this.scatterIcon.visible = true
    } catch {
      this.scatterIcon.visible = false
    }
  }

  /** 進入 Spine 演出：隱藏自繪 chrome、只顯示指定 Spine、數字歸零、重排版面。 */
  private beginSpine(spine: Spine, mode: 'intro' | 'settle' | 'fgSettle'): void {
    this.mode = mode
    this.spineActive = true
    this.activeSpine = spine
    this.skipRequested = false
    // 上一張字卡淡出期間的殘留點擊不可帶進這一張（否則新字卡會一出現就被關掉）。
    this.pendingDismiss = false
    // 預設「已達最終」（單擊即關閉）；有逐段跑分的大獎慶祝會另設為 false。
    this.phaseSettled = true
    this.clearCoins()
    this.panel.visible = false
    this.headline.visible = false
    this.captionTop.visible = false
    this.captionBottom.visible = false
    this.hideCardExtras()
    for (const s of [this.winSpine, this.cutsceneSpine, this.maxWinSpine]) {
      if (s !== null) s.visible = s === spine
    }
    this.numberProxy.value = 0
    this.bigNumber.setText(formatMoney(0))
    this.bigNumber.visible = true
    this.layout()
  }

  /** 設定 Spine 動畫序列：intro 播一次 → loop 循環（動畫不存在時安全略過）。 */
  private playSpineAnim(spine: Spine, intro: string, loop: string): void {
    const data = spine.skeleton.data
    const hasIntro = data.findAnimation(intro) !== null
    const hasLoop = data.findAnimation(loop) !== null
    if (hasIntro) {
      spine.state.setAnimation(0, intro, false)
      if (hasLoop) spine.state.addAnimation(0, loop, true, 0)
    } else if (hasLoop) {
      spine.state.setAnimation(0, loop, true)
    }
  }

  // ── fallback 自繪（Spine 不可用時） ──

  /** 進入自繪模式：隱藏 Spine 層、顯示自繪 chrome。 */
  private beginFallback(mode: 'intro' | 'settle'): void {
    this.mode = mode
    this.spineActive = false
    this.activeSpine = null
    this.skipRequested = false
    this.pendingDismiss = false
    this.phaseSettled = true
    this.hideCardExtras()
    for (const s of [this.winSpine, this.cutsceneSpine, this.maxWinSpine]) {
      if (s !== null) s.visible = false
    }
    this.panel.visible = true
    this.headline.visible = true
    this.bigNumber.visible = true
  }

  /** 自繪結算字卡：大獎分級標題／贏得／{金額}。tier!=none 額外噴金幣。Spine 不可用時使用。 */
  private async legacySettle(totalWin: number, tier: WinTier): Promise<void> {
    this.beginFallback('settle')
    const isBigWin = tier !== 'none'
    this.headline.text = tierHeadline(tier)
    this.setHeadlineColor(TIER_COLOR[tier])
    this.captionTop.text = t('reward.won')
    this.captionTop.visible = true
    this.captionBottom.text = ''
    this.captionBottom.visible = false
    this.clearCoins()

    this.numberProxy.value = 0
    this.bigNumber.setText(formatMoney(0))

    this.phaseSettled = false
    this.layout()
    await this.fadeIn()
    if (isBigWin) this.burstCoins()
    await this.countUpBetween(0, totalWin, 1.1)
    this.phaseSettled = true
    await this.waitForDismiss(REWARD_AUTO_CLOSE_MS)
    await this.fadeOut()
  }

  private setHeadlineColor(color: number): void {
    this.headline.style.fill = color
  }

  private async fadeIn(): Promise<void> {
    this.visible = true
    killTweensOf(this)
    this.alpha = 0
    await tweenTo(this, { alpha: 1, duration: FADE_DURATION })
  }

  private async fadeOut(): Promise<void> {
    killTweensOf(this)
    killTweensOf(this.numberProxy)
    killTweensOf(this.bigNumber.scale)
    await tweenTo(this, { alpha: 0, duration: FADE_DURATION })
    this.clearCoins()
    this.spineActive = false
    this.activeSpine = null
    if (!this.destroyed) this.visible = false
  }

  /** 進場數字（免費次數/直接顯示金額）彈出強調。 */
  private popBigNumber(): void {
    killTweensOf(this.bigNumber.scale)
    this.bigNumber.scale.set(0.3)
    void tweenTo(this.bigNumber.scale, { x: 1, y: 1, duration: 0.5, ease: 'back.out(2.4)' })
  }

  /** 直接設定金額（無跑分）。 */
  private setNumber(value: number): void {
    killTweensOf(this.numberProxy)
    this.numberProxy.value = value
    this.bigNumber.setText(formatMoney(value))
  }

  /** 金額由 from 滾動到 to（顯示單位）；Linear（對照 Unity 跑分 Ease.Linear）。點擊跳最終值時提前結束。 */
  private async countUpBetween(from: number, to: number, duration: number): Promise<void> {
    killTweensOf(this.numberProxy)
    this.numberProxy.value = from
    this.bigNumber.setText(formatMoney(from))
    await new Promise<void>((resolve) => {
      let settled = false
      const finish = (): void => {
        if (settled) return
        settled = true
        resolve()
      }
      void tweenTo(this.numberProxy, {
        value: to,
        duration,
        ease: 'none',
        onUpdate: () => {
          this.bigNumber.setText(formatMoney(this.numberProxy.value))
          if (this.skipRequested) {
            killTweensOf(this.numberProxy)
            finish()
          }
        },
      }).then(finish)
    })
    if (!this.skipRequested) this.bigNumber.setText(formatMoney(to))
  }

  /** 停留：達時間（holdMs）或使用者在「已達最終階段」點擊其一即關閉（對應 Unity autoClose=5s／點擊關閉）。 */
  private waitForDismiss(holdMs: number): Promise<void> {
    // 淡入期間就按下的關閉點擊在此消化（handleTap 當時還沒有 resolver 可呼叫）。
    if (this.pendingDismiss) {
      this.pendingDismiss = false
      return Promise.resolve()
    }
    return new Promise((resolve) => {
      let settled = false
      const finish = (): void => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        this.dismissResolver = null
        resolve()
      }
      const timer = setTimeout(finish, holdMs)
      // handleTap 在 phaseSettled=true 時呼叫此 resolver 關閉。
      this.dismissResolver = finish
    })
  }

  /** 大獎金幣噴發特效（自繪粒子，僅 fallback 使用；Spine 版靠骨架自身金幣光效）。 */
  private burstCoins(): void {
    this.clearCoins()
    const cx = this.screenW / 2
    const cy = this.screenH / 2
    const radius = Math.max(10, Math.min(this.screenW, this.screenH) * 0.016)
    const count = 22
    for (let i = 0; i < count; i++) {
      const coin = new Graphics()
        .circle(0, 0, radius)
        .fill({ color: 0xf6c845 })
        .circle(0, 0, radius)
        .stroke({ color: 0xa9781b, width: 2 })
        .circle(0, 0, radius * 0.55)
        .stroke({ color: 0xfce39a, width: 2 })
      coin.position.set(cx, cy)
      this.coinLayer.addChild(coin)

      // 依索引決定散射方向與力度（不使用 Math.random，改由索引均勻分佈避免破壞可重現性）。
      const angle = (Math.PI * 2 * i) / count - Math.PI / 2
      const spread = this.screenW * (0.16 + 0.12 * ((i % 5) / 4))
      const targetX = cx + Math.cos(angle) * spread
      const peakY = cy - this.screenH * (0.12 + 0.1 * ((i % 3) / 2))
      const fallY = cy + this.screenH * 0.5
      const upDur = 0.4
      const downDur = 0.7

      void tweenTo(coin, { x: targetX, y: peakY, duration: upDur, ease: 'power2.out' }).then(() => {
        if (coin.destroyed) return
        void tweenTo(coin, { y: fallY, alpha: 0, duration: downDur, ease: 'power2.in' }).then(() => {
          if (!coin.destroyed) coin.destroy()
        })
      })
    }
  }

  /** 清除進行中的金幣特效（中斷 tween 並銷毀）。 */
  private clearCoins(): void {
    for (const coin of this.coinLayer.removeChildren()) {
      killTweensOf(coin)
      coin.destroy()
    }
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.screenW = width
    this.screenH = height
    this.orientation = orientation
    // resize 中斷進行中的金幣噴發，避免飛向舊版面座標。
    this.clearCoins()
    if (this.visible) this.layout()
  }

  private layout(): void {
    const width = this.screenW
    const height = this.screenH
    if (width === 0) return

    // 暗背景 α：進場卡與 FG 結算卡照 Unity prefab Background（FreeSpinsUI/SettleUI 皆 0.627），其餘沿用 0.72。
    const dimAlpha = this.mode === 'intro' || this.mode === 'fgSettle' ? 0.627 : 0.72
    this.dim.clear().rect(0, 0, width, height).fill({ color: 0x000000, alpha: dimAlpha })

    if (this.spineActive) {
      this.layoutSpine()
      return
    }
    this.layoutFallback()
  }

  /**
   * Spine 演出版面：Spine 置中等比縮放（骨架與疊加文字皆以 Unity 全螢幕 Canvas 設計座標編排）。
   * 縮放採 Unity CanvasScaler 的 Expand 模式（=min(w/refW, h/refH)，見 unityScaleLandscape/Portrait）；
   * 橫版 16:9 時等於 height/1080，但直版（ref 1080×1920）差異極大——先前一律用 height/1080，
   * 直版會把字卡放大逾兩倍而左右裁掉標題。
   * 進場字卡（intro）的疊加文字照 FreeSpinsUI.prefab 座標（中心原點、y 向上）：
   * 標題 y=300、次數 y=0（TMP fontSize 200）、FREE SPINS y=-132、提示 y=-214；
   * 結算類（settle）僅數字疊在字卡下半。
   */
  private layoutSpine(): void {
    const width = this.screenW
    const height = this.screenH
    const s =
      this.orientation === 'portrait' ? unityScalePortrait(width, height) : unityScaleLandscape(width, height)
    if (this.activeSpine !== null) {
      this.activeSpine.scale.set(s)
      this.activeSpine.position.set(width / 2, height / 2)
    }
    if (this.mode === 'intro') {
      const cx = width / 2
      const cy = height / 2
      // 版面對照 Unity FreeSpinsUI／實機截圖（設計座標偏移，s 為 Unity Expand 縮放）：
      // 恭喜(-330) → 你贏得了(-168) → 次數(0) → 免費旋轉(+140) → 收集說明兩行(+212/+268) → 自動開始(+452)。
      this.headline.style.fontSize = Math.max(34, 72 * s)
      this.headline.position.set(cx, cy - 330 * s)
      this.youWon.style.fontSize = Math.max(26, 52 * s)
      this.youWon.position.set(cx, cy - 168 * s)
      this.bigNumber.setHeight(Math.max(64, 168 * s))
      this.bigNumber.position.set(cx, cy)
      this.captionBottom.style.fontSize = Math.max(24, 52 * s)
      this.captionBottom.position.set(cx, cy + 140 * s)
      // 原生字圖版標題三件：照 FreeSpinsUI.prefab 節點（Congratulations (0,300) 924×246、
      // YouHaveWonImage (0,152) 680×126、FreeSpinsImage (0,−132) 415×96），等比 fit 進節點框。
      this.fitIntroSprite(this.introTitleSprite, cx, cy - 300 * s, 924 * s, 246 * s)
      this.fitIntroSprite(this.youHaveWonSprite, cx, cy - 152 * s, 680 * s, 126 * s)
      this.fitIntroSprite(this.introFreeSpinsSprite, cx, cy + 132 * s, 415 * s, 96 * s)
      // 收集說明字圖（tx_collectScatters）：橫版照 LandscapeUI CollectScattersImage (−0.6,−229.3)
      // 984×142；直版照 PoriraitUI 覆寫 (0.8,−182.8)、scale 0.8。未載入時走下方文字行版面。
      if (this.orientation === 'portrait') {
        this.fitIntroSprite(this.collectBanner, cx + 0.8 * s, cy + 182.8 * s, 984 * 0.8 * s, 142 * 0.8 * s)
      } else {
        this.fitIntroSprite(this.collectBanner, cx - 0.6 * s, cy + 229.3 * s, 984 * s, 142 * s)
      }
      // 收集說明文字行（字圖未載入的 fallback）：「收集」＋Scatter 圖示＋「以進行升級…」inline 置中。
      if (this.collectPre.visible) {
        const rowFont = Math.max(18, 28 * s)
        const rowY = cy + 212 * s
        this.collectPre.style.fontSize = rowFont
        this.collectPost.style.fontSize = rowFont
        const iconH = rowFont * 1.6
        let iconW = 0
        if (this.scatterIcon.visible && this.scatterIcon.texture.height > 0) {
          const ratio = this.scatterIcon.texture.width / this.scatterIcon.texture.height
          this.scatterIcon.height = iconH
          this.scatterIcon.width = iconH * ratio
          iconW = this.scatterIcon.width
        }
        const gap = rowFont * 0.35
        const totalW = this.collectPre.width + (iconW > 0 ? gap + iconW : 0) + gap + this.collectPost.width
        let curX = cx - totalW / 2
        this.collectPre.position.set(curX, rowY)
        curX += this.collectPre.width + (iconW > 0 ? gap : 0)
        if (iconW > 0) {
          this.scatterIcon.position.set(curX, rowY)
          curX += iconW + gap
        }
        this.collectPost.position.set(curX, rowY)
        this.collectReward.style.fontSize = rowFont
        this.collectReward.position.set(cx, cy + 268 * s)
      }
      this.captionTop.style.fontSize = Math.max(20, 30 * s)
      this.captionTop.position.set(cx, cy + 452 * s)
      return
    }
    if (this.mode === 'fgSettle') {
      const cx = width / 2
      const cy = height / 2
      // FG 結算卡照 Unity SettleUI.prefab 原值（中心原點、y 向上）：標題 (0,300) 626×90、
      // 你贏得了 (0,152) 680×126、金額 ValueText (0,0) 1000×250 fontSize200（位圖字以 168·s 等價字高、
      // 寬逾 1000·s 依 TMP autoSize 等比縮小）、提示 HintText (0,−214) fontSize38。
      // （LandscapeUI/PoriraitUI 對結算卡另有嵌套覆寫值，惟 fileID 重映射無法逐節點對回，維持基準值。）
      this.fitIntroSprite(this.settleTitleSprite, cx, cy - 300 * s, 626 * s, 90 * s)
      this.fitIntroSprite(this.youHaveWonSprite, cx, cy - 152 * s, 680 * s, 126 * s)
      this.bigNumber.setHeight(Math.max(48, 168 * s))
      const maxNumW = 1000 * s
      if (this.bigNumber.width > maxNumW) {
        this.bigNumber.setHeight(Math.max(24, (168 * s * maxNumW) / this.bigNumber.width))
      }
      // P4：金額往下移，避免蓋住狼臉（見 FG_SETTLE_NUMBER_OFFSET_Y）。
      this.bigNumber.position.set(cx, cy + FG_SETTLE_NUMBER_OFFSET_Y * s)
      this.captionTop.style.fontSize = Math.max(16, 38 * s)
      this.captionTop.position.set(cx, cy + 214 * s)
      return
    }
    // 大獎慶祝／最高贏分（ani_win/ani_maxWin，骨骼自帶標題美術）：金額照 Unity ValueText 原值置於
    // 骨架原點下方 offsetY（見 SETTLE_VALUE）。先前為 height×0.6（＝骨架原點下方僅 108 設計 px），
    // 數字會壓在角色臉／胸上；改為 280 後落在角色前方的桌面帶。
    // （ani_maxWin 無獨立 prefab 數值，其骨架 txt 錨點骨與 ani_win 幾乎同高〔222 vs 217〕，共用同組值。）
    const value = SETTLE_VALUE[this.orientation]
    const bitmapH = Math.max(48, value.fontSize * TMP_FONT_TO_BITMAP * s)
    this.bigNumber.setHeight(bitmapH)
    const maxNumW = value.maxWidth * s
    if (this.bigNumber.width > maxNumW) {
      // TMP autoSize 等價：寬逾框等比縮小，下限 fontSizeMin=18。
      this.bigNumber.setHeight(
        Math.max(TMP_MIN_FONT_SIZE * TMP_FONT_TO_BITMAP * s, (bitmapH * maxNumW) / this.bigNumber.width),
      )
    }
    this.bigNumber.position.set(width / 2, height / 2 + value.offsetY * s)
  }

  /** 把進場字卡字圖等比縮放進節點框（Unity Image PreserveAspect 等價）並置中於 (x, y)。 */
  private fitIntroSprite(sprite: Sprite, x: number, y: number, maxW: number, maxH: number): void {
    if (!sprite.visible || sprite.texture.width <= 1) return
    const scale = Math.min(maxW / sprite.texture.width, maxH / sprite.texture.height)
    sprite.scale.set(scale)
    sprite.position.set(x, y)
  }

  /** 自繪版面：金邊面板 + 標題/說明/數字（進場 caption 在下、結算 caption 在上）。 */
  private layoutFallback(): void {
    const width = this.screenW
    const height = this.screenH
    const cx = width / 2
    const cy = height / 2
    const pw = Math.min(720, width * 0.82)
    const ph = Math.min(420, height * 0.66)

    this.panel
      .clear()
      .roundRect(cx - pw / 2 - 5, cy - ph / 2 - 5, pw + 10, ph + 10, 30)
      .stroke({ color: 0xffcf5a, width: 5, alpha: 0.6 })
      .roundRect(cx - pw / 2, cy - ph / 2, pw, ph, 26)
      .fill({ color: 0x2a1042, alpha: 0.96 })
      .roundRect(cx - pw / 2, cy - ph / 2, pw, ph, 26)
      .stroke({ color: 0xffe9a6, width: 2, alpha: 0.85 })

    const headFont = Math.max(26, ph * 0.14)
    const bigFont = Math.max(48, ph * 0.3)
    const capFont = Math.max(18, ph * 0.075)
    this.headline.style.fontSize = headFont
    this.bigNumber.setHeight(bigFont)
    this.captionTop.style.fontSize = capFont
    this.captionBottom.style.fontSize = Math.max(20, ph * 0.09)

    if (this.mode === 'intro') {
      // 進場：標題（上）/ 次數（中，超大）/「免費遊戲」（下）。
      this.headline.position.set(cx, cy - ph * 0.28)
      this.bigNumber.position.set(cx, cy - ph * 0.02)
      this.captionBottom.position.set(cx, cy + ph * 0.28)
    } else {
      // 結算：分級標題（上）/「贏得」（中上小字）/ 金額（中下，超大）。
      this.headline.position.set(cx, cy - ph * 0.3)
      this.captionTop.position.set(cx, cy - ph * 0.05)
      this.bigNumber.position.set(cx, cy + ph * 0.2)
    }
  }
}
